import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import cs from "./locales/cs.json";

// 14 supported European languages – must match public.site_languages
export const SUPPORTED_LANGS = [
  "cs", "sk", "en", "de", "pl", "hu", "fr", "es",
  "it", "nl", "pt", "ro", "uk", "ru",
] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

export const DEFAULT_LANG: SupportedLang = "cs";

// Rough country → language mapping for IP-based fallback detection.
const COUNTRY_TO_LANG: Record<string, SupportedLang> = {
  CZ: "cs",
  SK: "sk",
  GB: "en", IE: "en", US: "en", AU: "en", NZ: "en", CA: "en", MT: "en",
  DE: "de", AT: "de", CH: "de", LI: "de", LU: "de",
  PL: "pl",
  HU: "hu",
  FR: "fr", BE: "fr", MC: "fr",
  ES: "es",
  IT: "it", SM: "it", VA: "it",
  NL: "nl",
  PT: "pt", BR: "pt",
  RO: "ro", MD: "ro",
  UA: "uk",
  RU: "ru", BY: "ru", KZ: "ru",
};

function normalize(input?: string | null): SupportedLang | null {
  if (!input) return null;
  const short = input.toLowerCase().split("-")[0];
  return (SUPPORTED_LANGS as readonly string[]).includes(short)
    ? (short as SupportedLang)
    : null;
}

// Fetch approximate country from a public IP-geolocation service.
// Runs once per session and only if no user choice / matching browser lang.
async function detectByIP(): Promise<SupportedLang | null> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2500);
    const res = await fetch("https://ipapi.co/json/", { signal: controller.signal });
    clearTimeout(timeout);
    if (!res.ok) return null;
    const data = await res.json();
    const code = String(data?.country_code || "").toUpperCase();
    return COUNTRY_TO_LANG[code] ?? null;
  } catch {
    return null;
  }
}

const STORAGE_KEY = "app_lang";

// Map hostnames to a preferred default language.
// Used when the user hasn't picked a language manually yet.
const HOST_TO_LANG: Record<string, SupportedLang> = {
  "artisticcarpentry.eu": "en",
  "www.artisticcarpentry.eu": "en",
};

function initialDetection(): SupportedLang {
  // 1. Manual pick
  const stored = normalize(localStorage.getItem(STORAGE_KEY));
  if (stored) return stored;
  // 2. Domain-based default (e.g. artisticcarpentry.eu → en)
  if (typeof window !== "undefined") {
    const host = window.location.hostname.toLowerCase();
    if (HOST_TO_LANG[host]) return HOST_TO_LANG[host];
  }
  // 3. Browser language
  const nav = normalize(navigator.language) ||
    normalize(navigator.languages?.[0]);
  if (nav) return nav;
  // Fallback – IP kicks in async below
  return DEFAULT_LANG;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      cs: { translation: cs },
    },
    lng: initialDetection(),
    fallbackLng: DEFAULT_LANG,
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: STORAGE_KEY,
      caches: ["localStorage"],
    },
  });

// Reflect language on <html lang>
const applyHtmlLang = (lng: string) => {
  document.documentElement.lang = lng;
};
applyHtmlLang(i18n.language);
i18n.on("languageChanged", applyHtmlLang);

// Ensure resources are loaded for the initially-detected language
if (i18n.language && i18n.language !== "cs") {
  void ensureLanguageLoadedAndApply(i18n.language);
}

async function ensureLanguageLoadedAndApply(lng: string) {
  const short = lng.split("-")[0];
  if (!(SUPPORTED_LANGS as readonly string[]).includes(short)) return;
  await ensureLanguageLoaded(short as SupportedLang);
  // Force i18next to re-render subscribers now that the bundle exists
  await i18n.changeLanguage(short);
}

// Lazy-load translation resources on demand (locales other than cs live in DB / edge fn)
export async function ensureLanguageLoaded(lng: SupportedLang) {
  if (lng === "cs") return;
  if (i18n.hasResourceBundle(lng, "translation")) return;
  try {
    const res = await fetch(
      `https://kqeyprucdiqylfavbayi.supabase.co/functions/v1/get-ui-translations?lang=${lng}`,
      { headers: { apikey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtxZXlwcnVjZGlxeWxmYXZiYXlpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzYyMDYyNDMsImV4cCI6MjA5MTc4MjI0M30.m0oZ695nQ6_yocEwblKTgPWFPQk59cp5Sfq0LH0Nz2I" } },
    );
    if (!res.ok) return;
    const data = await res.json();
    if (data?.translations) {
      i18n.addResourceBundle(lng, "translation", data.translations, true, true);
    }
  } catch {
    /* silent – fall back to cs */
  }
}

// Kick off IP detection only when we ended up on default AND user has no stored pick
if (typeof window !== "undefined" && !localStorage.getItem(STORAGE_KEY)) {
  const browserLang = normalize(navigator.language);
  if (!browserLang) {
    void detectByIP().then((lng) => {
      if (lng && lng !== i18n.language) {
        ensureLanguageLoaded(lng).finally(() => i18n.changeLanguage(lng));
      }
    });
  } else if (browserLang !== "cs") {
    void ensureLanguageLoaded(browserLang);
  }
}

export default i18n;
